import {
    PortableText,
    PortableTextReactComponents,
    toPlainText,
} from "@portabletext/react";
import { NextSeo } from "next-seo";
import Link from "next/link";
import Header from "../../components/Header/Header";
import Hero from "../../components/Hero/Hero";
import { urlFor } from "../../../lib/sanity";
import PostImage from "../../components/Sanity/Image/Image";
import Newsletter from "../../components/Footer/components/Newsletter/Newsletter";
import Footer from "../../components/Footer/Footer";
import dynamic from "next/dynamic";
import { formatDate, trimText } from "../../../common/helper";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { dracula } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { Children } from "react";

const SocialShare = dynamic(
    () => import("../../components/SocialShare/SocialShare"),
    { ssr: false }
);

const components: Partial<PortableTextReactComponents> = {
    types: {
        image: PostImage,
    },
    marks: {
        code: ({ children }) => (
            <code className="bg-gray-light text-gray-dark p-1 rounded">
                {children}
            </code>
        ),
    },
    block: {
        pre: ({ children }) => {
            const code = Children.map(children, function (child) {
                if (typeof child == "string") return child;
            }).join("\n");

            return (
                <SyntaxHighlighter language="javascript" style={dracula}>
                    {code}
                </SyntaxHighlighter>
            );
        },
    },
};

export default function Post({ preview, post }) {
    const { title, publishedAt, mainImage, body, author, categories } = post;

    return (
        <div>
            {preview && (
                <Link href="/api/exit-preview">
                    <a className="block fixed z-40 bottom-0 left-0 ml-8 mb-8">
                        <span className="btn-primary bg-red">Preview Mode</span>
                    </a>
                </Link>
            )}
            <Header />
            <NextSeo
                title={`${title}`}
                description={
                    body ? trimText(toPlainText(body), 200) + "..." : ""
                }
                canonical={`https://www.dzobs.com/blog/${post?.slug}`}
                openGraph={{
                    images: [
                        {
                            url:
                                mainImage && mainImage.hasOwnProperty("asset")
                                    ? urlFor(mainImage).url()
                                    : "https://www.dzobs.com/social.png",
                        },
                    ],
                }}
            />
            <Hero
                title={title}
                date={publishedAt ? formatDate(publishedAt) : ""}
                categories={categories}
                showPublishedLabel={true}
            />
            <article className="blog-post max-w-screen-md mx-auto px-6 lg:px-0 mb-8 mt-8 leading-loose">
                {mainImage && mainImage.hasOwnProperty("asset") && (
                    <figure className="mb-4">
                        <img
                            src={urlFor(mainImage).width(1200).url()}
                            className="w-full"
                        />
                        <figcaption className="text-sm text-gray-500 mt-1">
                            {mainImage.caption}
                        </figcaption>
                    </figure>
                )}

                <div role="text">
                    <PortableText value={body} components={components} />
                </div>

                {author && (
                    <div
                        role="footer"
                        className="grid grid-cols-1 md:grid-cols-2 md:align-center border-t border-gray w-full pt-8 mt-8 text-center md:text-left"
                    >
                        <div role="author md:block inline">
                            <Link
                                href="/blog/autor/[slug]"
                                as={`/blog/autor/${author.slug?.current}`}
                            >
                                <a className="inline-flex items-center">
                                    <img
                                        alt="blog"
                                        src={
                                            author.image
                                                ? urlFor(author.image)
                                                      .width(100)
                                                      .height(100)
                                                      .url()
                                                : ""
                                        }
                                        className="w-12 h-12 rounded-full flex-shrink-0 object-cover object-center"
                                    />
                                    <span className="flex-grow flex flex-col pl-4">
                                        <span className="title-font font-medium text-gray-900">
                                            <span>{author.name}</span>
                                        </span>
                                        <span className="text-gray-400 text-xs tracking-widest mt-0.5 uppercase">
                                            {author.title}
                                        </span>
                                    </span>
                                </a>
                            </Link>
                        </div>
                        <div role="categories" className="md:text-right">
                            <span className="text-sm text-gray-400 md:inline block mb-4 md:mb-0 mt-6 md:mt-0">
                                Kategorije:
                            </span>
                            {categories &&
                                categories.map((cat) => (
                                    <Link
                                        href="/blog/kategorija/[slug]"
                                        as={`/blog/kategorija/${cat.slug?.current}`}
                                    >
                                        <a>
                                            <span
                                                key={cat._id}
                                                className="px-6 py-2 md:ml-3 md:mr-0 mr-3 bg-gray-100 text-gray-400 rounded-full text-sm"
                                            >
                                                {cat.title}
                                            </span>
                                        </a>
                                    </Link>
                                ))}
                        </div>
                    </div>
                )}
            </article>

            <Newsletter bg="bg-gray-light" />
            <Footer />
            <SocialShare title={post.title} term="tekst" />
        </div>
    );
}
